import React, { useCallback, useEffect, useMemo, useState } from "react";
import { SelectOption } from "atoms/forms/select";
import DeprecatedShow from "atoms/wrappers/show";
import NotebookRecord from "models/view-models/notebook-record";
import SelectFormField from "molecules/form-fields/select-form-field";
import { useLocalization } from "utilities/hooks/use-localization";
import DeprecatedHide from "atoms/wrappers/hide";
import { DataConfiguration } from "constants/data-configuration";
import InputFormField from "molecules/form-fields/input-form-field";
import { ButtonStyles } from "atoms/constants/button-styles";
import { ButtonSizes } from "atoms/constants/button-sizes";
import Button from "atoms/buttons/button";
import { CollectionUtils } from "utilities/collection-utils";

interface SelectExistingOrNewNotebookInputProps {
    availableNotebooks: NotebookRecord[];
    onSelectedNotebookChange: (selectedNotebook: number | string) => void;
    selectedNotebook: number | string;
}

const BASE_CLASS_NAME = "c-select-existing-or-new-notebook-input";
const CREATE_NEW_NOTEBOOK_VALUE = 0;

const SelectExistingOrNewNotebookInput: React.FC<
    SelectExistingOrNewNotebookInputProps
> = ({ availableNotebooks, onSelectedNotebookChange, selectedNotebook }) => {
    const [selectedNotebookOption, setSelectedNotebookOption] =
        useState<number>();
    const [newNotebookName, setNewNotebookName] = useState("");
    const [showNewNotebookInput, setShowNewNotebookInput] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);
    const { t } = useLocalization();
    const isInputFormFieldValid =
        !hasInteracted || newNotebookName.trim().length > 0;

    const createNewNotebookOption: SelectOption<NotebookRecord, number> =
        useMemo(
            (): SelectOption<NotebookRecord, number> => ({
                label: "Create New Notebook",
                value: CREATE_NEW_NOTEBOOK_VALUE,
            }),
            []
        );

    const existingNotebookOptions: SelectOption<NotebookRecord, number>[] =
        useMemo((): SelectOption<NotebookRecord, number>[] => {
            if (CollectionUtils.isEmpty(availableNotebooks)) {
                return [];
            }

            return availableNotebooks.map((notebook) => ({
                data: notebook,
                label: notebook.name ?? "",
                value: notebook.id!,
            }));
        }, [availableNotebooks]);

    const notebookOptions: SelectOption<NotebookRecord, number>[] = useMemo(
        (): SelectOption<NotebookRecord, number>[] => [
            ...existingNotebookOptions,
            createNewNotebookOption,
        ],
        [createNewNotebookOption, existingNotebookOptions]
    );

    const resetNewNotebookNameFields = () => {
        setShowNewNotebookInput(false);
        setNewNotebookName("");
        setHasInteracted(false);
    };

    const cannotSetNumericNotebookId = useCallback(
        (targetNotebookId?: number): targetNotebookId is undefined => {
            return (
                CollectionUtils.isEmpty(availableNotebooks) ||
                targetNotebookId == null ||
                targetNotebookId < 1
            );
        },
        [availableNotebooks]
    );

    const setNumericNotebookId = useCallback(
        (targetNotebookId?: number): void => {
            if (cannotSetNumericNotebookId(targetNotebookId)) {
                setSelectedNotebookOption(CREATE_NEW_NOTEBOOK_VALUE);
                return;
            }

            const ownedNotebookIds = availableNotebooks
                .map((n) => n.id)
                .filter((id): id is number => id != null && id > 0);

            if (ownedNotebookIds.includes(targetNotebookId)) {
                setSelectedNotebookOption(targetNotebookId);
                return;
            }

            setSelectedNotebookOption(ownedNotebookIds[0]);
        },
        [availableNotebooks, cannotSetNumericNotebookId]
    );

    const handleNewNotebookNameChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const { value } = event.target;
        setNewNotebookName(value);
        setHasInteracted(true);
        onSelectedNotebookChange(value);
    };

    const handleNotebookSelectChange = (
        option?: SelectOption<NotebookRecord, number>
    ) => {
        setSelectedNotebookOption(option?.value);
        setShowNewNotebookInput(option?.value === CREATE_NEW_NOTEBOOK_VALUE);

        if (option?.value != null) {
            onSelectedNotebookChange(option.value);
        }
    };

    const handleSelectExistingNotebookButtonClick = () => {
        resetNewNotebookNameFields();

        if (typeof selectedNotebook === "number" && selectedNotebook > 0) {
            setSelectedNotebookOption(selectedNotebook);
            onSelectedNotebookChange(selectedNotebook);
        } else if (!CollectionUtils.isEmpty(availableNotebooks)) {
            const firstNotebookId = availableNotebooks[0].id;
            if (firstNotebookId != null && firstNotebookId > 0) {
                setSelectedNotebookOption(firstNotebookId);
                onSelectedNotebookChange(firstNotebookId);
            }
        }
    };

    useEffect(() => {
        if (typeof selectedNotebook === "number") {
            setNumericNotebookId(selectedNotebook);
        } else if (typeof selectedNotebook === "string") {
            setShowNewNotebookInput(true);
            setNewNotebookName(selectedNotebook);
        }
    }, [selectedNotebook, setNumericNotebookId]);

    return (
        <div className={BASE_CLASS_NAME}>
            <DeprecatedHide when={showNewNotebookInput}>
                <SelectFormField
                    cssClassName={`${BASE_CLASS_NAME}__select`}
                    label={t("notebook")}
                    onChange={handleNotebookSelectChange}
                    options={notebookOptions}
                    value={selectedNotebookOption}
                />
            </DeprecatedHide>
            <DeprecatedShow when={showNewNotebookInput}>
                <InputFormField
                    label={t("notebook")}
                    maxLength={DataConfiguration.ShortTitleLength}
                    onChange={handleNewNotebookNameChange}
                    placeholder="Enter Your Notebook Name"
                    value={newNotebookName}
                    isValid={isInputFormFieldValid}
                />
                <Button
                    cssClassName={`${BASE_CLASS_NAME}__existing-notebook-button`}
                    onClick={handleSelectExistingNotebookButtonClick}
                    size={ButtonSizes.Small}
                    style={ButtonStyles.Text}>
                    Select Existing Notebook
                </Button>
            </DeprecatedShow>
        </div>
    );
};

export default SelectExistingOrNewNotebookInput;
